/**
 * ROOM VERIFY — shared domain.
 *
 * Pure values and pure functions only: no I/O, no dependencies, no environment access.
 * The Worker and both frontends import this module so that "11 photos" means exactly the
 * same thing on the phone, in the report, and in the completion gate.
 *
 * The server still recomputes every rule itself. This module exists for consistency,
 * never as a substitute for server-side validation.
 */
export const BUSINESS_TIMEZONE = 'Asia/Riyadh';
/** Report sections in the order they are captured and printed. */
export const SECTION_ORDER = [
    'ROOM_NUMBER',
    'BATHROOM',
    'KITCHEN',
    'ROOM',
];
/** Rooms that carry a kitchenette and therefore need the extra KITCHEN section. */
export const KITCHEN_ROOMS = ['105', '205', '305', '405', '505', '605', '705'];
const KITCHEN_ROOM_SET = new Set(KITCHEN_ROOMS);
export const isKitchenRoom = (roomNumber) => KITCHEN_ROOM_SET.has(String(roomNumber).trim());
/** A room number is 3 or 4 digits. Nothing else is accepted, in any layer. */
export const ROOM_NUMBER_PATTERN = /^\d{3,4}$/;
export const isValidRoomNumber = (roomNumber) => typeof roomNumber === 'string' && ROOM_NUMBER_PATTERN.test(roomNumber.trim());
export const isInspectionType = (value) => value === 'CHECKOUT' || value === 'CLEAN_ROOM';
export const isPhotoSection = (value) => typeof value === 'string' && SECTION_ORDER.includes(value);
/**
 * How many photos each section needs for a given room.
 * A section with 0 is not part of that room's inspection at all.
 */
export function sectionRequirements(roomNumber) {
    return {
        ROOM_NUMBER: 1,
        BATHROOM: 4,
        KITCHEN: isKitchenRoom(roomNumber) ? 4 : 0,
        ROOM: 6,
    };
}
/** 11 for a normal room, 15 for a kitchen room. */
export function requiredPhotoCount(roomNumber) {
    const reqs = sectionRequirements(roomNumber);
    return SECTION_ORDER.reduce((total, section) => total + reqs[section], 0);
}
/** The sections a given room actually has, in capture order. */
export function activeSections(roomNumber) {
    const reqs = sectionRequirements(roomNumber);
    return SECTION_ORDER.filter((section) => reqs[section] > 0);
}
/**
 * Standardised room item names.
 * These stay in English in every locale — they are inventory identifiers, not UI copy.
 */
export const STANDARD_ROOM_ITEMS = [
    'Small Towel',
    'Large Towel',
    'Bathrobe',
    'Scale',
    'Safe',
    'TV Remote',
    'Tissue Box',
    'Quran',
    'Prayer Mat',
    'Bathroom Mat',
];
const ROOM_ITEM_SET = new Set(STANDARD_ROOM_ITEMS);
export const isStandardRoomItem = (item) => typeof item === 'string' && ROOM_ITEM_SET.has(item);
export const OTHER_MISSING_ITEM_MAX_LENGTH = 200;
/**
 * Normalises and validates a room-items submission.
 *
 * "Missing items" with nothing actually selected is an invalid state, not an empty list —
 * it means the supervisor opened the branch and never chose anything. Completion must not
 * proceed on it.
 */
export function validateRoomItems(input) {
    const rawOther = typeof input.otherMissingItem === 'string'
        ? input.otherMissingItem.trim().slice(0, OTHER_MISSING_ITEM_MAX_LENGTH)
        : '';
    if (input.complete === true) {
        return { ok: true, value: { complete: true, missingItems: [], otherMissingItem: null } };
    }
    if (!Array.isArray(input.missingItems))
        return { ok: false, reason: 'MISSING_ITEMS_INVALID' };
    const unknownItem = input.missingItems.find((item) => !isStandardRoomItem(item));
    if (unknownItem !== undefined)
        return { ok: false, reason: 'MISSING_ITEMS_INVALID' };
    const missingItems = [...new Set(input.missingItems)].sort((a, b) => STANDARD_ROOM_ITEMS.indexOf(a) - STANDARD_ROOM_ITEMS.indexOf(b));
    if (missingItems.length === 0 && rawOther.length === 0) {
        return { ok: false, reason: 'MISSING_ITEMS_EMPTY' };
    }
    return {
        ok: true,
        value: { complete: false, missingItems, otherMissingItem: rawOther || null },
    };
}
/* ------------------------------------------------------------------ *
 * Riyadh business dates
 *
 * The business day is Asia/Riyadh (UTC+03:00, no DST). Timestamps are stored
 * in UTC; the calendar day they belong to is derived, never assumed.
 * ------------------------------------------------------------------ */
const RIYADH_OFFSET_MINUTES = 3 * 60;
/** The Riyadh calendar date (YYYY-MM-DD) that a UTC instant falls on. */
export function riyadhBusinessDate(instant = new Date()) {
    const date = instant instanceof Date ? instant : new Date(instant);
    const shifted = new Date(date.getTime() + RIYADH_OFFSET_MINUTES * 60_000);
    return shifted.toISOString().slice(0, 10);
}
/** Riyadh wall-clock time of a UTC instant, e.g. "10:44 AM". */
export function riyadhTime(instant, locale = 'en-US') {
    const date = instant instanceof Date ? instant : new Date(instant);
    return new Intl.DateTimeFormat(locale, {
        timeZone: BUSINESS_TIMEZONE,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date);
}
/** Riyadh date + time, e.g. "21 Aug 2026, 10:44 AM". */
export function riyadhDateTime(instant, locale = 'en-US') {
    const date = instant instanceof Date ? instant : new Date(instant);
    return new Intl.DateTimeFormat(locale, {
        timeZone: BUSINESS_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date);
}
export const BUSINESS_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const isBusinessDate = (value) => typeof value === 'string' && BUSINESS_DATE_PATTERN.test(value);
/** "1h 04m" / "4m 12s" / "38s" — duration for humans, never a bare second count. */
export function formatDuration(totalSeconds) {
    if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0)
        return '—';
    const seconds = Math.floor(totalSeconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0)
        return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0)
        return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
}
/* ------------------------------------------------------------------ *
 * Upload constraints — one source of truth for client and server
 * ------------------------------------------------------------------ */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/webp', 'image/png'];
export const MAX_UPLOAD_BYTES = 15_000_000;
/** Long edge of the normalised capture. Aspect ratio is always preserved. */
export const CAPTURE_MAX_EDGE = 1600;
export const CAPTURE_JPEG_QUALITY = 0.82;
//# sourceMappingURL=index.js.map